/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { SftpServ } from '../services/api/SftpServ';
import { saveAs } from 'file-saver';

function formatSize(bytes) {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatDate(iso) {
    if (!iso) return '-';
    return new Date(iso).toLocaleString();
}

function fileIcon(item) {
    if (item.isDirectory) return 'pi pi-folder';
    const ext = item.name.split('.').pop().toLowerCase();
    if (['html', 'htm'].includes(ext)) return 'pi pi-code';
    if (['php'].includes(ext)) return 'pi pi-server';
    if (['css'].includes(ext)) return 'pi pi-palette';
    if (['js'].includes(ext)) return 'pi pi-cog';
    if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext)) return 'pi pi-image';
    return 'pi pi-file';
}

function isEditable(name) {
    const ext = name.split('.').pop().toLowerCase();
    return ['html', 'htm', 'php', 'css', 'js', 'txt', 'json', 'xml'].includes(ext);
}

function pathJoin(...parts) {
    return parts.join('/').replace(/\/+/g, '/') || '/';
}

function getPublicUrl(domainLabel, domain, currentPath, fileName) {
    const rootPrefix = `/${domain}`;
    let relative = currentPath.startsWith(rootPrefix) ? currentPath.slice(rootPrefix.length) : currentPath;
    relative = relative.replace(/^\/+/, '');
    const filePath = relative ? `${relative}/${fileName}` : fileName;
    return `http://${domainLabel}/${filePath}`;
}

export default function EmbeddedFileExplorer({ domain, domainLabel, campaignId , pocId}) {
    console.log('EmbeddedFileExplorer pocId:', pocId);
    const rootPath = `/${domain}`;
    const campaignFolder = campaignId ? `A${campaignId}` : null;
    const campaignPath = campaignFolder ? `${rootPath}/${campaignFolder}` : rootPath;

    const toast = useRef(null);
    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);

    const [currentPath, setCurrentPath] = useState(campaignPath);
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [actionItem, setActionItem] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);
    const [selected, setSelected] = useState(null);

    const [renameDialog, setRenameDialog] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const [renameBusy, setRenameBusy] = useState(false);
    const [mkdirDialog, setMkdirDialog] = useState(false);
    const [mkdirValue, setMkdirValue] = useState('');
    const [mkdirBusy, setMkdirBusy] = useState(false);
    const [editDialog, setEditDialog] = useState(false);
    const [editContent, setEditContent] = useState('');
    const [editPath, setEditPath] = useState('');
    const [editLoading, setEditLoading] = useState(false);

    const [viewDialog, setViewDialog] = useState(false);
    const [viewContent, setViewContent] = useState('');
    const [viewPath, setViewPath] = useState('');
    const [viewLoading, setViewLoading] = useState(false);

    // when domain or campaignId changes, reset path and ensure folder exists
    useEffect(() => {
        if (!domain || !campaignId) return;
        const target = campaignFolder ? campaignPath : rootPath;
        setCurrentPath(target);
    }, [domain, campaignId]);

    const load = useCallback(async () => {
        if (!domain || !currentPath || currentPath.includes('undefined')) return;
        setLoading(true);
        setSelected(null);
        setSearch('');
        try {
            const data = await SftpServ.list(currentPath);
            setItems(data.items);
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: e.message });
        } finally {
            setLoading(false);
        }
    }, [currentPath]);

    useEffect(() => { load(); }, [load]);

    const breadcrumbs = () => {
        const parts = currentPath.split('/').filter(Boolean);
        return [{ label: 'htdocs', path: '/' }, ...parts.map((p, i) => ({
            label: p,
            path: '/' + parts.slice(0, i + 1).join('/')
        }))];
    };

    const canGoUp = currentPath !== rootPath;

    const goUp = () => {
        if (!canGoUp) return;
        const parts = currentPath.split('/').filter(Boolean);
        parts.pop();
        const up = parts.length ? '/' + parts.join('/') : '/';
        setCurrentPath(up.startsWith(rootPath) ? up : rootPath);
    };

    const handleOpen = (item) => {
        if (item.isDirectory) setCurrentPath(pathJoin(currentPath, item.name));
    };

    const handleDownload = async (item) => {
        setActionItem(item.name);
        try {
            const blob = await SftpServ.download(pathJoin(currentPath, item.name));
            saveAs(blob, item.name);
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Download failed', detail: e.message });
        } finally {
            setActionItem(null);
        }
    };

    const handleDelete = (item) => {
        confirmDialog({
            message: `Delete "${item.name}"?`,
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                setActionItem(item.name);
                try {
                    await SftpServ.delete(pathJoin(currentPath, item.name), item.isDirectory);
                    toast.current?.show({ severity: 'success', summary: 'Deleted' });
                    load();
                } catch (e) {
                    toast.current?.show({ severity: 'error', summary: 'Delete failed', detail: e.message });
                } finally {
                    setActionItem(null);
                }
            }
        });
    };

    const handleRenameOpen = (item) => {
        setRenameValue(item.name);
        setSelected(item);
        setRenameDialog(true);
    };

    const handleRenameSubmit = async () => {
        setRenameBusy(true);
        try {
            await SftpServ.rename(pathJoin(currentPath, selected.name), pathJoin(currentPath, renameValue));
            toast.current?.show({ severity: 'success', summary: 'Renamed' });
            setRenameDialog(false);
            load();
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Rename failed', detail: e.message });
        } finally {
            setRenameBusy(false);
        }
    };

    const handleMkdirSubmit = async () => {
        setMkdirBusy(true);
        try {
            await SftpServ.mkdir(pathJoin(currentPath, mkdirValue));
            toast.current?.show({ severity: 'success', summary: 'Folder created' });
            setMkdirDialog(false);
            setMkdirValue('');
            load();
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Failed', detail: e.message });
        } finally {
            setMkdirBusy(false);
        }
    };

    const handleViewOpen = async (item) => {
        const fp = pathJoin(currentPath, item.name);
        setViewPath(fp);
        setViewLoading(true);
        setViewDialog(true);
        try {
            const data = await SftpServ.readFile(fp);
            setViewContent(data.content);
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Read failed', detail: e.message });
            setViewDialog(false);
        } finally {
            setViewLoading(false);
        }
    };

    const handleEditOpen = async (item) => {
        const fp = pathJoin(currentPath, item.name);
        setEditPath(fp);
        setEditLoading(true);
        setEditDialog(true);
        try {
            const data = await SftpServ.readFile(fp);
            setEditContent(data.content);
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Read failed', detail: e.message });
            setEditDialog(false);
        } finally {
            setEditLoading(false);
        }
    };

    const handleEditSave = async () => {
        setSavingEdit(true);
        try {
            await SftpServ.writeFile(editPath, editContent);
            toast.current?.show({ severity: 'success', summary: 'Saved' });
            setEditDialog(false);
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Save failed', detail: e.message });
        } finally {
            setSavingEdit(false);
        }
    };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    setUploadProgress(0);

    try {
        await SftpServ.upload(
            currentPath,
            files,
            [],
            (pct) => setUploadProgress(pct),
            pocId // Pass the POC ID
        );

        toast.current?.show({
            severity: "success",
            summary: "Uploaded",
            detail: `${files.length} file(s)`
        });

        load();
    } catch (err) {
        toast.current?.show({
            severity: "error",
            summary: "Upload failed",
            detail: err.message
        });
    } finally {
        setUploading(false);
        setUploadProgress(0);
    }

    e.target.value = "";
};

    const handleFolderUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        const relativePaths = files.map(f => f.webkitRelativePath || f.name);
        setUploading(true);
        setUploadProgress(0);
        try {
            console.log('Folder upload pocId:', pocId);
await SftpServ.upload(
    currentPath,
    files,
    relativePaths,
    (pct) => setUploadProgress(pct),
    pocId
);            toast.current?.show({ severity: 'success', summary: 'Folder uploaded', detail: `${files.length} file(s)` });
            load();
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Upload failed', detail: err.message });
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
        e.target.value = '';
    };

    const filteredItems = search.trim()
        ? items.filter(item => item.name.toLowerCase().includes(search.toLowerCase()))
        : items;

    return (
        <div style={{ marginTop: '1.5rem' }}>
            <Toast ref={toast} />

            {/* Title row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <i className="pi pi-server" style={{ color: '#3b82f6' }} />
                <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                    <span style={{ color: '#64748b' }}>{domainLabel || domain}</span>
                    {campaignFolder && (
                        <span> / <span style={{ color: '#3b82f6' }}>{campaignFolder}</span></span>
                    )}
                </span>
            </div>

            {/* Toolbar row — centered */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ position: 'relative' }}>
                    <i className="pi pi-search" style={{ position: 'absolute', left: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.82rem' }} />
                    <InputText
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search in folder..."
                        style={{ paddingLeft: '2rem', paddingRight: search ? '2rem' : '0.5rem', height: '2rem', fontSize: '0.82rem', width: '180px' }}
                    />
                    {search && (
                        <i className="pi pi-times" onClick={() => setSearch('')}
                            style={{ position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.75rem', cursor: 'pointer' }} />
                    )}
                </span>
                <Button icon="pi pi-refresh" className="p-button-text p-button-sm" tooltip="Refresh" onClick={load} />
                <Button icon="pi pi-plus" className="p-button-text p-button-sm" tooltip="New Folder" onClick={() => setMkdirDialog(true)} />
                <Button icon="pi pi-upload" className="p-button-text p-button-sm" tooltip="Upload Files" onClick={() => fileInputRef.current.click()} />
                <Button icon="pi pi-folder-open" className="p-button-text p-button-sm" tooltip="Upload Folder" onClick={() => folderInputRef.current.click()} />
                <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleUpload} />
                <input ref={folderInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFolderUpload}
                    {...{ webkitdirectory: '', directory: '' }} />
            </div>

            {/* Upload progress */}
            {uploading && (
                <div style={{ padding: '0.4rem 0.75rem', background: '#eff6ff', borderBottom: '1px solid #bfdbfe' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#3b82f6', marginBottom: '0.25rem' }}>
                        <span><i className="pi pi-spin pi-spinner" style={{ marginRight: '0.4rem' }} />Uploading...</span>
                        <span>{uploadProgress}%</span>
                    </div>
                    <div style={{ background: '#dbeafe', borderRadius: '99px', height: '6px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${uploadProgress}%`, background: '#3b82f6', borderRadius: '99px', transition: 'width 0.2s ease' }} />
                    </div>
                </div>
            )}

            {/* Breadcrumb */}
            <div style={{ padding: '0.4rem 0.5rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px 4px 0 0', fontSize: '0.82rem', color: '#475569' }}>
                {breadcrumbs().map((crumb, i) => (
                    <span key={i}>
                        {i > 0 && <span style={{ color: '#94a3b8', margin: '0 2px' }}> / </span>}
                        <span
                            style={{ cursor: 'pointer', color: '#3b82f6', fontWeight: 500 }}
                            onClick={() => { if (crumb.path === '/' || crumb.path.startsWith(rootPath)) setCurrentPath(crumb.path === '/' ? rootPath : crumb.path); }}
                        >
                            {crumb.label}
                        </span>
                    </span>
                ))}
            </div>

            {/* File table */}
            <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 4px 4px', maxHeight: '420px', overflowY: 'auto' }}>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                        <ProgressSpinner style={{ width: '36px', height: '36px' }} />
                    </div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                        <thead>
                            <tr style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}>
                                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Name</th>
                                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0', width: '80px' }}>Size</th>
                                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0', width: '140px' }}>Modified</th>
                                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0', width: '200px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {canGoUp && (
                                <tr style={{ cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }} onClick={goUp}>
                                    <td colSpan={4} style={{ padding: '0.4rem 0.75rem', color: '#64748b', fontStyle: 'italic' }}>
                                        <i className="pi pi-arrow-up" style={{ marginRight: '0.4rem' }} /> ..
                                    </td>
                                </tr>
                            )}
                            {filteredItems.length === 0 && (
                                <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                    {search ? `No results for "${search}"` : 'Empty folder'}
                                </td></tr>
                            )}
                            {filteredItems.map(item => (
                                <tr
                                    key={item.name}
                                    style={{ cursor: 'pointer', borderBottom: '1px solid #f1f5f9', background: selected?.name === item.name ? '#dbeafe' : 'transparent' }}
                                    onClick={() => setSelected(item)}
                                    onDoubleClick={() => handleOpen(item)}
                                >
                                    <td style={{ padding: '0.4rem 0.75rem', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        <i className={`${fileIcon(item)}`} style={{ marginRight: '0.5rem', color: item.isDirectory ? '#f59e0b' : '#94a3b8' }} />
                                        {item.name}
                                    </td>
                                    <td style={{ padding: '0.4rem 0.75rem', color: '#64748b' }}>{item.isDirectory ? '-' : formatSize(item.size)}</td>
                                    <td style={{ padding: '0.4rem 0.75rem', color: '#64748b' }}>{formatDate(item.modifiedAt)}</td>
                                    <td style={{ padding: '0.4rem 0.75rem' }} onClick={e => e.stopPropagation()}>
                                        <div style={{ display: 'flex', gap: '0.1rem' }}>
                                            {item.isDirectory ? (
                                                <Button icon="pi pi-arrow-right" className="p-button-text p-button-sm" tooltip="Open" onClick={() => handleOpen(item)} />
                                            ) : (
                                                <>
                                                    <Button
                                                        icon={actionItem === item.name ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
                                                        className="p-button-text p-button-sm"
                                                        tooltip="Download"
                                                        disabled={!!actionItem}
                                                        onClick={() => handleDownload(item)}
                                                    />
                                                    <Button
                                                        icon={viewLoading && viewPath.endsWith(item.name) ? 'pi pi-spin pi-spinner' : 'pi pi-eye'}
                                                        className="p-button-text p-button-sm"
                                                        tooltip="View code"
                                                        disabled={!!actionItem}
                                                        onClick={() => handleViewOpen(item)}
                                                    />
                                                    <Button
                                                        icon="pi pi-link"
                                                        className="p-button-text p-button-sm"
                                                        tooltip="Copy link"
                                                        disabled={!!actionItem}
                                                        onClick={() => {
                                                            const url = getPublicUrl(domainLabel || domain, domain, currentPath, item.name);
                                                            navigator.clipboard.writeText(url);
                                                            toast.current?.show({ severity: 'success', summary: 'Link copied', detail: url, life: 3000 });
                                                        }}
                                                    />
                                                    {isEditable(item.name) && (
                                                        <Button
                                                            icon={editLoading && editPath.endsWith(item.name) ? 'pi pi-spin pi-spinner' : 'pi pi-pencil'}
                                                            className="p-button-text p-button-sm"
                                                            tooltip="Edit"
                                                            disabled={!!actionItem}
                                                            onClick={() => handleEditOpen(item)}
                                                        />
                                                    )}
                                                </>
                                            )}
                                            <Button
                                                icon="pi pi-file-edit"
                                                className="p-button-text p-button-sm"
                                                tooltip="Rename"
                                                disabled={!!actionItem}
                                                onClick={() => handleRenameOpen(item)}
                                            />
                                            <Button
                                                icon={actionItem === item.name ? 'pi pi-spin pi-spinner' : 'pi pi-trash'}
                                                className="p-button-text p-button-sm p-button-danger"
                                                tooltip="Delete"
                                                disabled={!!actionItem}
                                                onClick={() => handleDelete(item)}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Rename Dialog */}
            <Dialog header="Rename" visible={renameDialog} onHide={() => !renameBusy && setRenameDialog(false)} style={{ width: '360px' }}>
                <div style={{ marginBottom: '1rem' }}>
                    <InputText value={renameValue} onChange={e => setRenameValue(e.target.value)} style={{ width: '100%' }} onKeyDown={e => e.key === 'Enter' && !renameBusy && handleRenameSubmit()} autoFocus />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <Button label="Cancel" className="p-button-text" disabled={renameBusy} onClick={() => setRenameDialog(false)} />
                    <Button label="Rename" icon={renameBusy ? 'pi pi-spin pi-spinner' : 'pi pi-check'} disabled={renameBusy} onClick={handleRenameSubmit} />
                </div>
            </Dialog>

            {/* New Folder Dialog */}
            <Dialog header="New Folder" visible={mkdirDialog} onHide={() => !mkdirBusy && setMkdirDialog(false)} style={{ width: '360px' }}>
                <div style={{ marginBottom: '1rem' }}>
                    <InputText placeholder="Folder name" value={mkdirValue} onChange={e => setMkdirValue(e.target.value)} style={{ width: '100%' }} onKeyDown={e => e.key === 'Enter' && !mkdirBusy && handleMkdirSubmit()} autoFocus />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <Button label="Cancel" className="p-button-text" disabled={mkdirBusy} onClick={() => setMkdirDialog(false)} />
                    <Button label="Create" icon={mkdirBusy ? 'pi pi-spin pi-spinner' : 'pi pi-check'} disabled={mkdirBusy} onClick={handleMkdirSubmit} />
                </div>
            </Dialog>

            {/* View File Dialog */}
            <Dialog header={`View: ${viewPath}`} visible={viewDialog} onHide={() => setViewDialog(false)} style={{ width: '80vw', maxWidth: '1100px' }} maximizable>
                {viewLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}><ProgressSpinner /></div>
                ) : (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.4rem' }}>
                            <Button
                                label="Copy Code"
                                icon="pi pi-copy"
                                className="p-button-text p-button-sm"
                                onClick={() => {
                                    navigator.clipboard.writeText(viewContent);
                                    toast.current?.show({ severity: 'success', summary: 'Copied', detail: 'Code copied to clipboard', life: 2000 });
                                }}
                            />
                        </div>
                        <textarea
                            readOnly
                            style={{ width: '100%', height: '55vh', fontFamily: 'Courier New, monospace', fontSize: '0.85rem', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '0.75rem', background: '#1e293b', color: '#e2e8f0', lineHeight: 1.6, resize: 'vertical', boxSizing: 'border-box', cursor: 'default' }}
                            value={viewContent}
                        />
                    </>
                )}
            </Dialog>

            {/* Edit File Dialog */}
            <Dialog header={`Edit: ${editPath}`} visible={editDialog} onHide={() => !savingEdit && setEditDialog(false)} style={{ width: '80vw', maxWidth: '1100px' }} maximizable>
                {editLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}><ProgressSpinner /></div>
                ) : (
                    <>
                        <textarea
                            style={{ width: '100%', height: '55vh', fontFamily: 'Courier New, monospace', fontSize: '0.85rem', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '0.75rem', background: '#1e293b', color: '#e2e8f0', lineHeight: 1.6, resize: 'vertical', boxSizing: 'border-box' }}
                            value={editContent}
                            onChange={e => setEditContent(e.target.value)}
                            spellCheck={false}
                        />
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                            <Button label="Cancel" className="p-button-text" disabled={savingEdit} onClick={() => setEditDialog(false)} />
                            <Button label="Save" icon={savingEdit ? 'pi pi-spin pi-spinner' : 'pi pi-save'} disabled={savingEdit} onClick={handleEditSave} />
                        </div>
                    </>
                )}
            </Dialog>
        </div>
    );
}
